import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { DestroyOptions } from "pixi.js";

import { sound } from "@pixi/sound";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { Manager } from "../../core/Manager";
import { SoundManager } from "../../core/SoundManager";
import { EventConstants } from "../../gameClientEvents/EventConstants";
import { GameConstants } from "../../gameClientEvents/GameConstants";
import { GameEvents } from "../../gameClientEvents/GameEvents";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";

export class GameIntro extends DisplayObject {
    private gameIntro: Spine | null = null;
    private introTransition: Spine | null = null;

    private hasCompleted: boolean = false;
    private isTransitionPlaying: boolean = false;

    constructor(config: IDisplayObject) {
        super(config);

        this.init();
    }

    public override destroy(options?: DestroyOptions): void {
        this.cleanupGameIntro();
        this.cleanupTransition();

        super.destroy(options);
    }

    private init(): void {
        this.gameIntro = Spine.from({
            skeleton: "gameIntroSkel",
            atlas: "gameIntroAtlas"
        });

        this.gameIntro.label = "gameIntro";

        this.addChild(this.gameIntro);

        this.setPositionResize(Manager.Orientation || "LANDSCAPE");

        this.play();
    }

    public play(): void {
        if (!this.gameIntro) return;

        this.gameIntro.state.addListener({
            start: () => {
                SoundManager.play(SoundConstants.INTRO_MUSIC, {
                    volume: 0.8,
                });
            },

            event: (_entry, event) => {
                if (event.data.name === "transition") {
                    this.playTransition();
                }
            },

            complete: () => {
                // Do nothing here.
                // We wait for the transition to finish.
            }
        });

        this.gameIntro.eventMode = "static";
        this.gameIntro.interactive = true;

        // this.gameIntro.on("pointerup", this.onIntroComplete, this);

        this.gameIntro.state.setAnimation(0, "animation", false);
    }

    private playTransition(): void {
        if (!this.gameIntro) return;
        if (this.isTransitionPlaying) return;

        this.isTransitionPlaying = true;

        this.introTransition = Spine.from({
            skeleton: "bannerTransitionSkel",
            atlas: "bannerTransitionAtlas"
        });

        this.introTransition.label = "introTransition";

        this.addChild(this.introTransition);

        // Optional debug positioning
        // this.introTransition.position.set(0, 0);
        // this.introTransition.scale.set(1);

        this.introTransition.state.addListener({
            start: () => {
                sound.play(SoundConstants.INTRO_TRANSITION, { volume: 0.7 });
            },

            event: (_entry, event) => {
                if (event.data.name === "change") {
                    this.cleanupGameIntro();
                }
            },

            complete: () => {
                this.onIntroComplete();
            }
        });

        this.introTransition.eventMode = "static";
        this.introTransition.interactive = true;

        // this.introTransition.on("pointerup", this.onIntroComplete, this);

        this.introTransition.state.setAnimation(0, "transition", false);
    }

    private onIntroComplete(): void {
        if (this.hasCompleted) return;

        this.hasCompleted = true;

        SoundManager.stop("Intro_transition");

        this.cleanupGameIntro();
        this.cleanupTransition();

        GameEvents.emitter.emit(EventConstants.GAME_INTRO_COMPLETE);

        requestAnimationFrame(() => {
            if (!this.destroyed) {
                this.destroy();
            }
        });
    }

    private cleanupGameIntro(): void {
        if (!this.gameIntro) return;

        this.gameIntro.off("pointerup", this.onIntroComplete, this);

        this.gameIntro.state.clearListeners();

        this.removeChild(this.gameIntro);

        this.gameIntro.destroy({
            children: true
        });

        this.gameIntro = null;
    }

    private cleanupTransition(): void {
        if (!this.introTransition) return;

        this.introTransition.off("pointerup", this.onIntroComplete, this);

        this.introTransition.state.clearListeners();

        this.removeChild(this.introTransition);

        this.introTransition.destroy({
            children: true
        });

        this.introTransition = null;
    }

    public override setPositionResize(orientation: string): void {
        const BG_SCALE_FACTOR = Math.max(
            Manager.screenwidth / GameConstants.BACKGROUND_SIZE,
            Manager.screenHeight / GameConstants.BACKGROUND_SIZE
        );

        let scale: number;
        let bgScale: number;

        if (orientation === "LANDSCAPE") {
            scale = Math.min(
                Manager.screenwidth / 1920,
                Manager.screenHeight / GameConstants.GAME_RES_HEIGHT
            );
        } else {
            scale = Math.min(
                Manager.screenwidth / 1080,
                Manager.screenHeight / GameConstants.GAME_RES_WIDTH
            );
        }

        bgScale = Math.max(scale, BG_SCALE_FACTOR);

        this.scale.set(bgScale);

        const bgXPos = Manager.screenwidth / 2;
        const bgYPos = Manager.screenHeight / 2;

        this.position.set(bgXPos, bgYPos);
    }
}